import {
  List,
  Datagrid,
  TextField,
  EditButton,
  Edit,
  SimpleForm,
  TextInput,
  Create,
  required,
  BooleanField,
  BooleanInput,
  DateField,
  SelectInput,
  Show,
  TabbedShowLayout,
  Tab,
  SearchInput,
  FunctionField,
  useRecordContext,
  useNotify,
  TabbedForm,
  FormTab,
  ArrayInput,
  SimpleFormIterator,
  PasswordInput,
} from "react-admin";
import { Box, Chip, Stack, Tooltip, IconButton } from "@mui/material";
import { LockReset } from "@mui/icons-material";
import { RoleManager } from "../../components/RoleManager";

// Custom components for enhanced UX
const UserStatusChip = () => {
  const record = useRecordContext();
  if (!record) return null;

  const getStatusColor = (user: Record<string, unknown>) => {
    if (!user.emailVerified) return "error";
    if (!user.onboardingCompleted) return "warning";
    return "success";
  };

  const getStatusText = (user: Record<string, unknown>) => {
    if (!user.emailVerified) return "Unverified";
    if (!user.onboardingCompleted) return "Incomplete";
    return "Active";
  };

  return (
    <Chip
      label={getStatusText(record)}
      color={getStatusColor(record)}
      size="small"
    />
  );
};

const RoleChips = () => {
  const record = useRecordContext();
  if (!record?.roles) return null;

  return (
    <Stack direction="row" spacing={0.5}>
      {(record.roles as Record<string, unknown>[]).map(
        (roleObj: Record<string, unknown>, index: number) => (
          <Chip
            key={index}
            label={roleObj.role || roleObj}
            size="small"
            color="primary"
            variant="outlined"
          />
        ),
      )}
    </Stack>
  );
};

// Password Reset Button
const PasswordResetButton = () => {
  const record = useRecordContext();
  const notify = useNotify();

  const handleResetPassword = async () => {
    if (!record?.id) return;

    try {
      const response = await fetch(
        `/api/admin/users/${record.id}/reset-password`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        notify("Password reset email sent successfully", { type: "success" });
      } else {
        notify("Failed to send password reset email", { type: "error" });
      }
    } catch {
      notify("Error sending password reset email", { type: "error" });
    }
  };

  return (
    <Tooltip title="Send password reset email">
      <IconButton onClick={handleResetPassword} color="warning">
        <LockReset />
      </IconButton>
    </Tooltip>
  );
};

// User List Component
export const UserList = () => (
  <List
    filters={[
      <SearchInput
        key="search"
        source="search"
        placeholder="Search by name or email"
        alwaysOn
      />,
      <SelectInput
        key="role"
        source="role"
        choices={[
          { id: "superAdmin", name: "Super Admin" },
          { id: "admin", name: "Admin" },
          { id: "support", name: "Support" },
          { id: "regular", name: "Regular" },
        ]}
        alwaysOn
      />,
      <SelectInput
        key="onboardingCompleted"
        source="onboardingCompleted"
        choices={[
          { id: true, name: "Completed" },
          { id: false, name: "Incomplete" },
        ]}
      />,
    ]}
    sort={{ field: "createdAt", order: "DESC" }}
  >
    <Datagrid rowClick="show">
      <TextField source="username" label="Email" />
      <TextField source="name" />
      <TextField source="phoneNumber" label="Phone" />
      <TextField source="residency" />
      <RoleChips />
      <UserStatusChip />
      <BooleanField source="emailVerified" label="Verified" />
      <BooleanField source="onboardingCompleted" label="Onboarded" />
      <DateField source="createdAt" label="Created" showTime />
      <EditButton />
    </Datagrid>
  </List>
);

// User Show Component
export const UserShow = () => {
  const record = useRecordContext();

  return (
    <Show>
      <TabbedShowLayout>
        <Tab label="Personal Information">
          <TextField source="id" />
          <TextField source="name" />
          <TextField source="username" label="Email" />
          <TextField source="phoneNumber" label="Phone" />
          <TextField source="residency" />
          <TextField source="avatar" label="Avatar URL" />
        </Tab>

        <Tab label="Account Status">
          <FunctionField
            label="Roles"
            render={(record) => (
              <Stack direction="row" spacing={1}>
                {(record?.roles as Record<string, unknown>[])?.map(
                  (roleObj: Record<string, unknown>, index: number) => (
                    <Chip
                      key={index}
                      label={roleObj.role || roleObj}
                      color="primary"
                      variant="outlined"
                    />
                  ),
                )}
              </Stack>
            )}
          />
          <BooleanField source="emailVerified" label="Email Verified" />
          <BooleanField
            source="onboardingCompleted"
            label="Onboarding Completed"
          />
          <BooleanField source="isOAuthAccount" label="OAuth Account" />
        </Tab>

        <Tab label="System Information">
          <DateField source="createdAt" label="Created At" showTime />
          <DateField source="updatedAt" label="Updated At" showTime />
        </Tab>

        <Tab label="Actions">
          {record && (
            <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
              <RoleManager record={record} resource="users" />
              <PasswordResetButton />
            </Box>
          )}
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

// User Edit Component
export const UserEdit = () => (
  <Edit>
    <TabbedForm>
      <FormTab label="Profile">
        <TextInput source="name" validate={required()} />
        <TextInput source="phoneNumber" label="Phone Number" />
        <TextInput source="residency" label="Country Code (e.g., US, CA)" />
        <BooleanInput
          source="onboardingCompleted"
          label="Onboarding Completed"
        />
      </FormTab>

      <FormTab label="Roles">
        <ArrayInput source="roles" label="User Roles">
          <SimpleFormIterator>
            <SelectInput
              source="role"
              choices={[
                { id: "superAdmin", name: "Super Admin" },
                { id: "admin", name: "Admin" },
                { id: "support", name: "Support" },
                { id: "regular", name: "Regular" },
              ]}
            />
          </SimpleFormIterator>
        </ArrayInput>
      </FormTab>
    </TabbedForm>
  </Edit>
);

// User Create Component
export const UserCreate = () => (
  <Create>
    <SimpleForm>
      <TextInput
        source="username"
        label="Email"
        type="email"
        validate={required()}
      />
      <PasswordInput source="password" validate={required()} />
      <TextInput source="name" validate={required()} />
      <TextInput source="phoneNumber" label="Phone Number" />
      <TextInput
        source="residency"
        label="Country Code (e.g., US, CA)"
        validate={required()}
      />

      <ArrayInput source="roles" label="User Roles" validate={required()}>
        <SimpleFormIterator>
          <SelectInput
            source="role"
            choices={[
              { id: "superAdmin", name: "Super Admin" },
              { id: "admin", name: "Admin" },
              { id: "support", name: "Support" },
              { id: "regular", name: "Regular" },
            ]}
          />
        </SimpleFormIterator>
      </ArrayInput>

      <BooleanInput source="emailVerified" label="Mark email as verified" />
    </SimpleForm>
  </Create>
);
